/**
 * Project Enhancement Service
 * Enhances project data with localStorage thumbnails for instant loading
 */

import localThumbnailService from './localThumbnailService';

class ProjectEnhancementService {
    constructor() {
        this.thumbnailService = localThumbnailService;
    }

    /**
     * Enhance projects with thumbnails from localStorage
     * This provides instant thumbnail loading without backend calls
     */
    enhanceProjectsWithThumbnails(projects) {
        if (!projects || !Array.isArray(projects)) {
            console.warn('⚠️ Invalid projects data provided');
            return projects;
        }

        // Check if cache is ready
        if (!this.thumbnailService.isCacheReady()) {
            console.log('📦 Thumbnail cache not ready, projects will use fallback thumbnails');
            return projects;
        }

        // Get project IDs
        const projectIds = projects.map(project => project.Id || project.id).filter(Boolean);
        
        if (projectIds.length === 0) {
            console.log('📦 No valid project IDs found');
            return projects;
        }

        // Get thumbnails from localStorage
        const thumbnails = this.thumbnailService.getThumbnailsForProjects(projectIds);
        
        console.log(`📦 Enhancing ${projects.length} projects with ${Object.keys(thumbnails).length} thumbnails from localStorage`);

        // Enhance projects with thumbnails
        const enhancedProjects = projects.map(project => {
            const projectId = project.Id || project.id;
            const thumbnailUrl = thumbnails[projectId];
            
            if (thumbnailUrl) {
                return {
                    ...project,
                    ThumbnailURL: thumbnailUrl,
                    ThumbnailSource: 'localStorage' // Flag to indicate source
                };
            }
            
            return project; // Keep original if no thumbnail found
        });

        return enhancedProjects;
    }

    /**
     * Get cache statistics for debugging
     */
    getCacheStats() {
        return this.thumbnailService.getCacheStats();
    }

    /**
     * Force refresh cache
     */
    forceRefreshCache() {
        this.thumbnailService.forceRefreshCache();
    }

    /**
     * Check if cache is ready
     */
    isCacheReady() {
        return this.thumbnailService.isCacheReady();
    }
}

// Create singleton instance
const projectEnhancementService = new ProjectEnhancementService();

export default projectEnhancementService;
